import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

export interface Order {
  _id: string;
  orderNumber: string;
  dailyNumber?: number; // Branch-specific daily sequence for KDS display
  dailyNumberDate?: string; // YYYY-MM-DD for tracking daily reset
  branchId: string | { _id: string; name: string; code: string };
  customerId?: string;
  items: OrderItem[];
  status: 'new' | 'in_progress' | 'ready' | 'picked_up' | 'completed' | 'cancelled' | 'voided';
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  payments: Payment[];
  isPaidInFull: boolean;
  createdBy?: { _id: string; username: string; email?: string };
  completedAt?: string; // When all items are done
  createdAt: string;
  updatedAt: string;
}

export interface ItemStatusHistory {
  status: 'pending' | 'preparing' | 'done';
  timestamp: string;
  updatedBy?: string;
}

export interface OrderItem {
  _id?: string;
  menuItemId: string;
  menuItemName: string;
  quantity: number;
  size: 'S' | 'M' | 'L' | 'small' | 'medium' | 'large';
  sweetnessLevel?: number;
  sweetness?: string;
  toppings?: Array<{
    toppingId: string;
    name: string;
    price: number;
  }>;
  modifiers?: Array<{
    modifierGroupId: string;
    modifierOptionId: string;
    name: string;
    priceAdjustment: number;
  }>;
  recipeId?: string;
  formulaId?: string;
  wasAutoSwitched?: boolean;
  unitPrice: number;
  totalPrice: number;
  notes?: string;
  // Item-level KDS tracking
  itemStatus?: 'pending' | 'preparing' | 'done';
  itemStatusHistory?: ItemStatusHistory[];
  stationId?: string; // For station-based routing
}

export interface Payment {
  method: 'cash' | 'card' | 'qr' | 'loyalty_points';
  amount: number;
  reference?: string;
  paidAt: string;
}

export interface OrdersResponse {
  orders: Order[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Order filters interface
export interface OrderFilters {
  status?: string;
  isPaidInFull?: boolean | null;
  search?: string;
  startDate?: string;
  endDate?: string;
}

// Fetch all orders for a branch with optional filters and pagination
export function useOrders(
  branchId?: string,
  filters: OrderFilters = {},
  page: number = 1,
  limit: number = 20
) {
  return useQuery({
    queryKey: ['orders', branchId, filters, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (filters.status) params.append('status', filters.status);
      if (filters.isPaidInFull !== null && filters.isPaidInFull !== undefined) {
        params.append('isPaidInFull', String(filters.isPaidInFull));
      }
      if (filters.search) params.append('search', filters.search);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await apiClient.get<APIResponse<OrdersResponse>>(
        `/orders?${params.toString()}`
      );
      return response.data.data;
    },
    enabled: !!branchId,
  });
}

// Fetch active orders (new, in_progress, ready) for KDS
export function useActiveOrders(branchId?: string, includeCompleted: boolean = false) {
  return useQuery({
    queryKey: ['orders', 'active', branchId, includeCompleted],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      // Fetch all active statuses for KDS
      const statuses = includeCompleted
        ? 'new,in_progress,ready,picked_up,completed'
        : 'new,in_progress,ready';
      params.append('status', statuses);
      params.append('limit', '100'); // Get more orders for KDS

      const response = await apiClient.get<APIResponse<{ orders: Order[] }>>(
        `/orders?${params.toString()}`
      );
      return response.data.data.orders;
    },
    refetchInterval: 3000, // Faster refresh for KDS (< 200ms target with Redis)
  });
}

// Fetch single order by ID
export function useOrder(orderId: string) {
  return useQuery({
    queryKey: ['orders', orderId],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ order: Order }>>(
        `/orders/${orderId}`
      );
      return response.data.data.order;
    },
    enabled: !!orderId,
  });
}

// Fetch orders for a specific customer (for loyalty page)
export function useCustomerOrders(customerId?: string, limit: number = 10) {
  return useQuery({
    queryKey: ['orders', 'customer', customerId, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('customerId', customerId!);
      params.append('limit', limit.toString());

      const response = await apiClient.get<APIResponse<OrdersResponse>>(
        `/orders?${params.toString()}`
      );
      return response.data.data.orders;
    },
    enabled: !!customerId,
  });
}
